const { windowLoaded, documentReady } = require('@grpn/document-states');
const { setShellEventOrRun } = require('@grpn/shell-page');
const domConfig = require('../../../page/frontend/helpers/dom_config');
const PaginationSeoLinks = require('../../../pagination/frontend/helpers/seo_links.coffee');
const DataFetcher = require('./helpers/data-fetcher');
const Spinner = require('./helpers/spinner');
const PaginationRenderer = require('./helpers/pagination-renderer');
const carouselScrollPositionsHelper = require('../../../page/frontend/helpers/carousel_scroll_positions');
const isTTDLandingPage = require('../../../page/frontend/helpers/is_ttd_landing_page');
const urlUpdate = require('../../../page/frontend/helpers/url_update');
const QueryParams = require('../../../page/frontend/helpers/params');
const createUrlSearchParams = require('@grpn/filters/lib/create_url_search_params');
const scrollPositionHelper = require('../../../page/frontend/helpers/scroll_position');
const mapHelper = require('../../common/helpers/map-helper');
const {
  AVAILABLE_ID,
  MAP_ID,
  BBOX_ID,
} = require('@grpn/filters/lib/facet_ids');

const PAGE_PARAM = 'page';
const SELECTORS = {
  INLINE_AD: '#pull-dfp-inline-slot',
  CARDS: '#pull-results',
  NO_CARDS: '#pull-no-cards',
  NO_CARDS_GETAWAYS: '.getaways_notification',
  NO_MAP_CARDS: '#pull-no-map-cards',
};

const context = {
  initialize() {
    setShellEventOrRun(() => {
      documentReady.then(() => context.onReady());
      windowLoaded.then(() => context.onLoad());
    });
  },
  setDefaults() {
    context.domConfig = domConfig.get() || {};
  },
  updateMapButton({ lazyLoadedCards, paginatedCards } = {}) {
    const { desktopFilters, filtersRendered } = context;
    if (!desktopFilters) {
      return;
    }

    if (lazyLoadedCards) {
      context.showMapButton =
        context.showMapButton ||
        mapHelper.hasShowMapButtonInCardSlots(lazyLoadedCards);
    }

    if (paginatedCards) {
      context.showMapButton = mapHelper.hasShowMapButtonInCardSlots(
        paginatedCards
      );
    }

    if (!filtersRendered) {
      return;
    }

    if (context.showMapButton) {
      desktopFilters.showRefinement(MAP_ID);
    } else {
      desktopFilters.resetRefinement(MAP_ID);
      desktopFilters.hideRefinement(MAP_ID);
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(MAP_ID);
      const stringifiedSearchParams = searchParams.toString();
      const updatedUrl = `${window.location.pathname}${
        stringifiedSearchParams.length ? `?${stringifiedSearchParams}` : ''
      }`;
      urlUpdate.update(updatedUrl);
      context.handleMapState(searchParams);
    }
  },
  initializeObjects() {
    const { csrfToken, showMapButton } = context.domConfig;
    context.mapContainerElement = document.querySelector('.pull-map-container');
    context.showMapButton = showMapButton;
    context.spinner = new Spinner();
    context.dataFetcher = new DataFetcher(csrfToken);
    context.paginationRenderer = new PaginationRenderer();
    context.intersectionObserver = new IntersectionObserver(
      this.onScrollFetchLazyLoadedCardsHandler,
      {
        root: null,
        rootMargin: '0px 0px 600px 0px',
        threshold: 0.01,
      }
    );
    context.updateUrlWhenNoCards();
  },
  getPageNumberFromUrl(url) {
    let page = 1;

    const queryParams = url.split('?')[1];

    if (queryParams && queryParams.length) {
      const queryParamHash = Groupon.Utils.unparam(queryParams);

      if (queryParamHash.page) {
        page = queryParamHash.page;
      }
    }

    return page;
  },
  handleOnPopState() {
    const newUrlParams = Groupon.Utils.unparam();

    context.fetchCards(newUrlParams);
    context.dataFetcher
      .fetchFacets()
      .then(context.handleFacetsUpdatedSuccess)
      .catch(context.handleFacetsUpdatedError);

    context.handleMapState(newUrlParams);
  },
  attachListeners() {
    window.addEventListener('popstate', context.handleOnPopState);
  },
  lazyLoadAssets() {
    const {
      cardUiJavascript,
      filtersJavascript,
      thingsToDoJavascript,
      enabledFeatures,
    } = context.domConfig;

    window.Groupon.Utils.loadAsyncJS(
      cardUiJavascript,
      context.onLazyLoadedCardUiJavascript
    );

    window.Groupon.Utils.loadAsyncJS(
      filtersJavascript,
      context.onLazyLoadedFiltersJavascript
    );

    if (enabledFeatures && enabledFeatures.isTTDPage) {
      window.Groupon.Utils.loadAsyncJS(
        thingsToDoJavascript,
        context.onLazyLoadedThingsToDoJavascript
      );
    }
  },
  onReady() {
    PaginationSeoLinks.set();
    document.body.classList.add('pull-app-is-ready');
  },
  onLoad() {
    context.setDefaults();
    context.initializeObjects();
    context.lazyLoadAssets();
    context.restorePreviousState();
    context.attachListeners();
    context.loadAds();
    document.body.classList.add('pull-app-is-loaded');
  },
  lazyLoadRemainingDeals() {},
  onLazyLoadedCardUiJavascript() {
    window.CardUI.init();
    window.GIG.Modal.init();
    document.body.classList.add('pull-app-card-ui-js-lazy-loaded');
  },
  onLazyLoadedFiltersJavascript() {
    context.featuredFiltersContainer = document.getElementById(
      'featured-filters-ui-container'
    );

    context.bookingFiltersContainer = document.getElementById(
      'booking-filters-ui-container'
    );

    context.getawaysSearchContainer = document.getElementById(
      'getaways-search-filters-ui-container'
    );

    context.desktopFilters = new window.FiltersUi();

    context.dataFetcher
      .fetchFacets()
      .then(context.handleFacetsFetchedSuccess)
      .catch(context.handleFacetsFetchedError);

    document.addEventListener(
      'refinementsUpdated',
      context.handleRefinementsUpdated
    );

    document.addEventListener('filtersRendered', context.handleFiltersRendered);
  },
  handleFiltersRendered() {
    context.filtersRendered = true;
    context.updateMapButton();
  },
  isNoResults(pagination) {
    if (!context.hasNoCards()) {
      return false;
    }
    if (!pagination) {
      return true;
    }
    return !pagination.count;
  },
  enableStickyFilters() {
    context.filtersObserver = new IntersectionObserver(
      context.onScrollStickyFilters
    );
    context.filtersObserver.observe(document.querySelector('.header-position'));
    context.scrollStickyFilterSheaderElement = document.querySelector(
      '.header-container'
    );
    context.scrollStickyFilterPlaceholderElement = document.querySelector(
      '.header-placeholder'
    );
  },
  onScrollStickyFilters(entries) {
    const headerPositionEntry = entries[0];

    if (!headerPositionEntry) {
      return;
    }

    const { isIntersecting } = headerPositionEntry;
    const placeholderElement = context.scrollStickyFilterPlaceholderElement;
    const filterHeaderElement = context.scrollStickyFilterSheaderElement;
    const offsetMagicNumber = 26;

    placeholderElement.style.height = `${
      filterHeaderElement.offsetHeight + offsetMagicNumber
    }px`;

    if (isIntersecting) {
      filterHeaderElement.classList.remove('sticky');
      placeholderElement.style.position = 'absolute';
    } else {
      filterHeaderElement.classList.add('sticky');
      placeholderElement.style.position = '';
    }
  },
  handleFacetsFetchedSuccess(json) {
    if (!json) {
      context.clearPageHeader();
      context.clearFilters();
      return;
    }

    const { pageHeader, facets, pagination } = json;

    context.handleUpdatePageHeader(pageHeader);

    if (!context.isNoResults(pagination) && facets) {
      context.desktopFilters.renderFeatured(
        facets,
        context.featuredFiltersContainer,
        {
          isMapInitiallyHidden: true,
        }
      );

      context.desktopFilters.renderBookingFilters(
        facets,
        context.bookingFiltersContainer
      );
      context.desktopFilters.renderGetawaysSearch(
        facets,
        context.getawaysSearchContainer
      );
    } else {
      context.featuredFiltersContainer.innerHTML = '';
    }

    context.enableStickyFilters();
    document.body.classList.add('pull-app-filters-ui-js-lazy-loaded');
  },
  // eslint-disable-next-line no-unused-vars
  handleFacetsFetchedError(e) {},
  handleFacetsUpdatedSuccess(json) {
    if (!json) {
      context.clearPageHeader();
      context.clearFilters();
      return;
    }

    const { pageTitle, pageHeader, pagination, facets, filterOptions } = json;

    if (pageTitle) {
      document.title = pageTitle;
    }

    context.handleUpdatePageHeader(pageHeader);

    if (facets) {
      context.desktopFilters.updateFeatured(
        facets,
        context.featuredFiltersContainer
      );
      const hasDeals = pagination && pagination.count;
      const searchParams = new URLSearchParams(location.search);
      const isAvailableSelected = searchParams.get(AVAILABLE_ID);
      if (hasDeals && !isAvailableSelected) {
        const { shouldShowBookingFilter } = filterOptions || {};
        context.desktopFilters.updateBookingFilters({
          facets,
          shouldShowBookingFilter,
        });
      }
    } else {
      context.clearFilters();
    }
  },
  handleFacetsUpdatedError() {},
  handleRefinementsUpdated(event) {
    // Temporary code so we don't have to change the old browse code
    // Can update the refinement selectedValues to the correct structure after we remove the old code
    if (
      event.detail &&
      event.detail.selectedRefinements &&
      event.detail.selectedRefinements.bbox
    ) {
      event.detail.selectedRefinements.bbox = {
        selectedValues: [{ id: event.detail.selectedRefinements.bbox }],
      };
    }
    const oldSearchParams = new URLSearchParams(location.search);
    const searchParams = createUrlSearchParams.create(event.detail);
    if (oldSearchParams.get(MAP_ID) === searchParams.get(MAP_ID)) {
      searchParams.delete(PAGE_PARAM);
    }

    const noMapCards = document.querySelector(SELECTORS.NO_MAP_CARDS);
    const shouldRemoveBbox = !searchParams.get(MAP_ID) && !!noMapCards;

    if (shouldRemoveBbox) {
      searchParams.delete(BBOX_ID);
    }

    const paramObject = createUrlSearchParams.paramsToObject(searchParams);
    const stringifiedSearchParams = searchParams.toString();
    const updatedUrl = `${window.location.pathname}${
      stringifiedSearchParams.length ? `?${stringifiedSearchParams}` : ''
    }`;

    urlUpdate.update(updatedUrl, { pushState: true });

    context.clearNoCardsMessage();

    if (noMapCards) {
      noMapCards.remove();
    }

    if (
      event.detail &&
      event.detail.options &&
      event.detail.options.isMapUpdate
    ) {
      paramObject.mapUpdate = true;
    }

    context.fetchCards(paramObject);
    context.dataFetcher
      .fetchFacets()
      .then(context.handleFacetsUpdatedSuccess)
      .catch(context.handleFacetsUpdatedError);
    context.handleMapState(paramObject);
  },
  handleUpdatePageHeader(pageHeader) {
    const pageHeaderElement = document.querySelector('.page-header');

    if (pageHeaderElement) {
      if (pageHeader) {
        pageHeaderElement.innerHTML = pageHeader;
      } else {
        context.clearPageHeader();
      }
    }
  },
  clearPageHeader() {
    const pageHeaderElement = document.querySelector('.page-header');

    if (pageHeaderElement) {
      pageHeaderElement.innerHTML = '';
    }
  },
  clearFilters() {
    const featuredFiltersContainer = document.getElementById(
      'featured-filters-ui-container'
    );
    if (featuredFiltersContainer) {
      featuredFiltersContainer.innerHTML = '';
    }
  },
  handleMapState(paramObject) {
    if (!paramObject.map) {
      context.mapContainerElement.classList.add('hide');
      return;
    } else {
      context.mapContainerElement.classList.remove('hide');
    }

    if (!window.BrowseMapsApplication) {
      const { mapsJavascript } = context.domConfig;

      window.Groupon.Utils.loadAsyncJS(
        mapsJavascript,
        context.onLazyLoadedMapsJavascript
      );
    } else {
      window.BrowseMapsApplication.loadMap();
    }
  },
  onLazyLoadedMapsJavascript() {
    window.BrowseMapsApplication.initialize();
    window.BrowseMapsApplication.loadMap();
    document.body.classList.add('pull-app-maps-js-lazy-loaded');
  },
  onLazyLoadedThingsToDoJavascript() {
    if (isTTDLandingPage()) {
      if (window.BrowseThingsToDoApplication) {
        window.BrowseThingsToDoApplicationfetchTargetedAudience();
      }
    }
    document.body.classList.add('pull-app-things-to-do-js-lazy-loaded');
  },
  attachOnScrollFetchLazyLoadedCardsListener() {
    const cardShells = document.querySelector('.cui-shell-card');
    if (cardShells) {
      context.intersectionObserver.observe(cardShells);
    }
  },
  onScrollFetchLazyLoadedCardsHandler() {
    context.fetchLazyLoadedCards();
    context.intersectionObserver.disconnect();
  },
  hasNoCards() {
    const noCardsMessage = document.querySelector(SELECTORS.NO_CARDS);
    const noCardsGetways = document.querySelector(SELECTORS.NO_CARDS_GETAWAYS);

    return noCardsMessage && !noCardsGetways;
  },
  fetchLazyLoadedCards() {
    const cardShells = document.querySelector('.cui-shell-card');
    if (!cardShells) {
      return;
    }

    if (document.querySelector(SELECTORS.NO_CARDS_GETAWAYS)) {
      context.paginationRenderer.clearCardShells();
      return;
    }

    context.dataFetcher
      .fetchLazyLoadedCards(context.getLazyLoadedSectionIndex())
      .then(context.handleLazyLoadedCardsFetchedSuccess)
      .catch(context.handleLazyLoadedCardsFetchedError);
  },
  fetchBottomSlot() {
    context.dataFetcher
      .fetchBottomSlot()
      .then(context.handleBottomSlotFetchedSuccess)
      .catch(context.handleBottomSlotFetchedError);
  },
  handleBottomSlotFetchedSuccess(bottomSlot) {
    context.paginationRenderer.insertBottomSlot(
      bottomSlot,
      context.domConfig.showSearchCollections
    );
  },
  handleBottomSlotFetchedError() {},
  updateUrlWhenNoCards() {
    if (context.hasNoCards()) {
      const updatedUrl = window.location.pathname;
      urlUpdate.update(updatedUrl);
    }
  },
  fetchCards(params) {
    const cardShells = document.querySelector('.cui-shell-card');
    if (cardShells) {
      return;
    }

    context.spinner.start();
    context.dataFetcher
      .fetchCards(params)
      .then(context.handlePaginatedCardsFetchedSuccess)
      .catch(context.handlePaginatedCardsFetchedError)
      .then(context.spinner.stop.bind(context.spinner));
  },
  handleLazyLoadedCardsFetchedError() {
    context.paginationRenderer.clearCardShells();
  },
  handleRestoreCardsFromState({ lazyLoadedCardsHtml, bottomWidgetHtml }) {
    if (!lazyLoadedCardsHtml) {
      return;
    }
    context.paginationRenderer.clearCardShells();
    context.paginationRenderer.restoreCardsHtmlFromState(lazyLoadedCardsHtml);
    context.paginationRenderer.restoreBottomWidgetHtmlFromState(
      bottomWidgetHtml
    );
    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );
    context.attachCardClickHandlers();
    context.loadAds();
  },
  handleLazyLoadedCardsFetchedSuccess(lazyLoadedCards) {
    context.paginationRenderer.clearCardShells();

    if (!lazyLoadedCards || !lazyLoadedCards.length) {
      return;
    }

    context.updateMapButton({ lazyLoadedCards });

    const scrollPos =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    context.paginationRenderer.insertLazyLoadedCards(lazyLoadedCards);
    window.scrollTo(0, scrollPos);
    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );

    context.attachCardClickHandlers();
    context.loadAds();
    context.attachOnScrollFetchLazyLoadedCardsListener();
  },
  handlePaginatedCardsFetchedError() {},
  handlePaginatedCardsFetchedSuccess(paginatedCards) {
    const {
      updateMapButton,
      paginationRenderer,
      attachCardClickHandlers,
      loadAds,
      onPaginationClick,
    } = context;

    if (!paginatedCards || !paginatedCards.length) {
      return;
    }

    updateMapButton({ paginatedCards });

    paginationRenderer.insertPaginatedCards(paginatedCards);
    paginationRenderer.scrollToTop();
    paginationRenderer.attachPaginationClickHandler(onPaginationClick);

    attachCardClickHandlers();
    loadAds();
    context.urlParams = Groupon.Utils.unparam();
  },
  hideElement(selector) {
    const el = document.querySelector(selector);
    if (el) {
      el.classList.add('hide');
    }
  },
  clearNoCardsMessage() {
    const noCardsMessage = document.querySelector(SELECTORS.NO_CARDS);
    if (noCardsMessage) {
      noCardsMessage.remove();
    }
  },
  onPaginationClick() {
    event.preventDefault();
    context.clearNoCardsMessage();

    const page = context.getPageNumberFromUrl(event.currentTarget.href);

    const paramObject = Groupon.Utils.unparam();
    paramObject.page = page;

    const updatedUrl = `${window.location.pathname}?${QueryParams.toString(
      paramObject
    )}`;
    urlUpdate.update(updatedUrl, { pushState: true });
    context.fetchCards(paramObject);
    context.handleMapState(paramObject);
  },
  attachCardClickHandlers() {
    const allCards = document.querySelectorAll(
      '.card-ui, .location-rememberance'
    );

    if (allCards && allCards.length) {
      allCards.forEach(card => {
        card.addEventListener('click', context.onCardCLicked);
      });
    }

    const getawaysReset = document.querySelector('.getaways_reset');
    if (getawaysReset) {
      getawaysReset.addEventListener('click', context.performGetawaysReset);
    }
  },
  performGetawaysReset() {
    context.clearNoCardsMessage();
    if (context.desktopFilters) {
      const resetOptions = {
        tryDifferentDate: true,
        update: true,
      };
      context.desktopFilters.resetRefinement('hotelAvailability', resetOptions);
    }
  },
  onCardCLicked() {
    const lazyLoadedCardsHtml = context.sanitizeCardsHtml(
      document.querySelectorAll('#pull-results > *')
    );

    const bottomWidgetHtml = context.sanitizeCardsHtml(
      document.querySelectorAll('#pull-bottom-slot > *')
    );
    const carouselScrollPositions = carouselScrollPositionsHelper.getCarouselScrollPositions();
    const scrollPosition = scrollPositionHelper.getScrollPosition();
    const showMapButton = context.showMapButton;

    context.setCardsAndScrollPositionInBrowserHistory({
      lazyLoadedCardsHtml,
      carouselScrollPositions,
      scrollPosition,
      showMapButton,
      bottomWidgetHtml,
    });
  },
  sanitizeCardsHtml(cardsHtml) {
    const combinedHtml = [];
    if (cardsHtml && cardsHtml.length) {
      cardsHtml.forEach(node => {
        let cardSlotHtml = node.outerHTML;

        if (node.classList.contains('pull-dfp')) {
          if (node.children.length)
            cardSlotHtml = cardSlotHtml.replace(node.children[0].innerHTML, '');
        } else {
          const dfpNode = node.querySelector('.pull-dfp');
          if (dfpNode && dfpNode.children.length) {
            cardSlotHtml = cardSlotHtml.replace(
              dfpNode.children[0].innerHTML,
              ''
            );
          }
        }

        combinedHtml.push({ cardSlotHtml });
      });
    }
    return combinedHtml;
  },
  getLazyLoadedSectionIndex() {
    const defaultIndex = 0;
    context.lazyLoadedSectionIndex =
      context.lazyLoadedSectionIndex == null
        ? defaultIndex
        : context.lazyLoadedSectionIndex;

    return context.lazyLoadedSectionIndex++;
  },
  restorePreviousState() {
    const state = history.state || {};
    const {
      lazyLoadedCardsHtml,
      lazyLoadedSectionIndex,
      carouselScrollPositions,
      scrollPosition,
      page,
      showMapButton,
      bottomWidgetHtml,
    } = state;

    if (
      lazyLoadedCardsHtml &&
      lazyLoadedSectionIndex &&
      carouselScrollPositions &&
      scrollPosition &&
      page === context.getPageNumberFromUrl(window.location.href)
    ) {
      context.showMapButton = showMapButton;
      context.updateMapButton();

      context.lazyLoadedSectionIndex = lazyLoadedSectionIndex;

      context.handleRestoreCardsFromState({
        lazyLoadedCardsHtml,
        bottomWidgetHtml,
      });

      carouselScrollPositionsHelper.restoreCarouselScrollPositions(
        carouselScrollPositions
      );

      window.scrollTo(0, scrollPosition);
    } else {
      context.fetchBottomSlot();

      if (context.hasNoCards()) {
        context.lazyloadPaginatedCards();
      } else {
        context.fetchLazyLoadedCards();
      }
    }

    context.setCardsAndScrollPositionInBrowserHistory({ scrollPosition: 0 });

    const paramObj = Groupon.Utils.unparam();
    context.handleMapState(paramObj);
  },
  lazyloadPaginatedCards() {
    const cardShells = document.querySelector('.cui-shell-card');
    if (!cardShells) {
      return;
    }

    context.dataFetcher
      .fetchCards(Groupon.Utils.unparam())
      .then(context.handleLazyLoadedCardsFetchedSuccess)
      .catch(context.handleLazyLoadedCardsFetchedError);
  },
  setCardsAndScrollPositionInBrowserHistory({
    lazyLoadedCardsHtml,
    carouselScrollPositions,
    scrollPosition,
    showMapButton,
    bottomWidgetHtml,
  }) {
    const page = context.getPageNumberFromUrl(window.location.href);

    const state = {
      lazyLoadedCardsHtml,
      lazyLoadedSectionIndex: context.lazyLoadedSectionIndex,
      carouselScrollPositions,
      scrollPosition,
      page,
      showMapButton,
      bottomWidgetHtml,
    };

    history.replaceState(state, `Page ${page}`, window.location.href);
  },
  hasInlineAd() {
    const inlineAd = document.querySelector(SELECTORS.INLINE_AD);
    return !!inlineAd && !inlineAd.getAttribute('data-is-placed');
  },
  getInlineAdPlacementElement() {
    const queryParams = window.location.href.split('?')[1];
    const queryParamHash = Groupon.Utils.unparam(queryParams);
    const { query: isSearch } = queryParamHash;

    const cardsPerRow = 3;
    const row = isSearch ? 10 : 5;
    let placementPosition = cardsPerRow * row;

    const cards = Array.from(
      document.querySelectorAll(
        `${SELECTORS.CARDS} .card-ui:not(.cui-shell-card)`
      )
    );
    const bandCardIndex = cards.findIndex(node =>
      node.classList.contains('cui-c-band')
    );
    if (bandCardIndex >= 0) {
      if (placementPosition > bandCardIndex) {
        const orphansBeforeBandCard = bandCardIndex % cardsPerRow;
        placementPosition =
          placementPosition + 1 - (cardsPerRow - orphansBeforeBandCard);
      }
      if (placementPosition === bandCardIndex + 1) {
        placementPosition = placementPosition - 1;
      }
    }

    return cards[placementPosition];
  },
  getInlineAdPlacementElementForSearchCollections() {
    const carousels = document.querySelectorAll(
      `${SELECTORS.CARDS} .card-ui.cui-c-compound`
    );
    if (carousels.length > 2) {
      return carousels[2];
    }
    return null;
  },
  placeInlineAd() {
    const aiInlineAd = document.querySelector(SELECTORS.INLINE_AD);
    if (aiInlineAd) {
      if (!context.domConfig.showSearchCollections) {
        const inlineAdPlacementElement = context.getInlineAdPlacementElement();

        if (inlineAdPlacementElement) {
          inlineAdPlacementElement.before(aiInlineAd);
          aiInlineAd.setAttribute('data-is-placed', 'true');
        }
      } else {
        const inlineAdPlacementElement = context.getInlineAdPlacementElementForSearchCollections();

        if (inlineAdPlacementElement) {
          inlineAdPlacementElement.parentElement.insertBefore(
            aiInlineAd,
            inlineAdPlacementElement
          );
          aiInlineAd.setAttribute('data-is-placed', 'true');
        }
      }
    }
  },
  loadAds(tryAmount = 0) {
    if (Groupon.DFPAds) {
      if (context.hasInlineAd()) {
        context.placeInlineAd();
      }
      Groupon.DFPAds.load();
    } else {
      if (tryAmount < 20) {
        setTimeout(() => {
          context.loadAds(++tryAmount);
        }, 100);
      }
    }
  },
};

export default context;
